import {
  Add,
  Campaign as CampaignIcon,
  Cancel,
  ContentCopy,
  Archive,
  Unarchive,
  Drafts,
  Edit,
  FilterAlt,
  MarkEmailRead,
  Pause,
  People,
  PlayArrow,
  Send,
} from '@mui/icons-material'
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { DataGridPro, GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro'
import { DesktopOnlyNotice } from 'components/DesktopOnlyNotice'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import {
  CAMPAIGN_REPEAT_LABEL,
  CAMPAIGN_STATUS_LABEL,
  CAMPAIGN_TYPE_LABEL,
  buildAudienceEvalContext,
  formatCampaignPercent,
  parseCampaignRepeat,
  previewAudienceEligibility,
  summarizeAudienceFilter,
  type CampaignRepeat,
  type CampaignStatus,
  type MarketingCampaign,
} from '@otw/models/leads/marketingCampaign'
import { getTerminalDisplayName } from '@otw/models/leads/terminals'
import React, { useMemo, useState } from 'react'
import { useContactCompanies } from 'hooks/contacts/useContactCompanies'
import { useContacts } from 'hooks/contacts/useContacts'
import { useMarketingAudiences } from 'hooks/contacts/useMarketingAudiences'
import { useMarketingCampaigns } from 'hooks/contacts/useMarketingCampaigns'
import { usePickupLocations } from 'hooks/inventory/usePickupLocations'
import { useTerminals } from 'hooks/terminals/useTerminals'
import {
  getUserDisplayName,
  useInventoryAssignableUsers,
  useSalesAssignableUsers,
  useUsers,
} from 'hooks/useUsers'
import { formatNumber } from '../../util/customerUtils'
import AudiencesPanel from '../campaigns/AudiencesPanel'
import CampaignFormDialog from '../dialogs/CampaignFormDialog'
import CampaignRecipientsDialog from '../campaigns/CampaignRecipientsDialog'
import { CrmPageActions } from '../CrmPageChrome'

interface CampaignsTabProps {
  isActive: boolean
}

type CampaignsView = 'campaigns' | 'audiences'

interface CampaignRow {
  id: string
  campaign: MarketingCampaign
  name: string
  subject: string
  typeLabel: string
  status: CampaignStatus
  statusLabel: string
  audienceName: string
  audienceSummary: string
  matchingCount: number
  eligibleCount: number
  sentCount: number | null
  failedCount: number | null
  unsubRate: string
  scheduledAt: Date | null
  repeat: CampaignRepeat
  createdBy: string
  updatedAt: Date
}

const PLACEHOLDER = '—'

const statusChipColor = (
  status: CampaignStatus,
): 'default' | 'info' | 'warning' | 'success' | 'error' => {
  if (status === 'sent') return 'success'
  if (status === 'sending' || status === 'paused') return 'warning'
  if (status === 'scheduled') return 'info'
  if (status === 'cancelled') return 'error'
  return 'default'
}

const canSendCampaignRow = (row: CampaignRow): boolean => {
  if (!row.name.trim() || !row.subject.trim()) return false
  if (row.status === 'sending') return true
  if (row.status === 'draft' || row.status === 'scheduled') return row.eligibleCount > 0
  return false
}

const canPauseCampaignRow = (row: CampaignRow): boolean =>
  row.status === 'scheduled' || row.status === 'sending'

const canResumeCampaignRow = (row: CampaignRow): boolean => row.status === 'paused'

const canCancelCampaignRow = (row: CampaignRow): boolean =>
  row.status === 'scheduled' || row.status === 'sending' || row.status === 'paused'

const CampaignsTab: React.FC<CampaignsTabProps> = ({ isActive }) => {
  const isMobile = useIsMobile()
  const theme = useTheme()
  const {
    campaigns,
    isLoading,
    duplicateCampaign,
    archiveCampaign,
    unarchiveCampaign,
    sendCampaign,
    pauseCampaign,
    resumeCampaign,
    cancelCampaign,
    isSaving,
    isArchiving,
    isSending,
    isUpdatingStatus,
  } = useMarketingCampaigns()
  const { audiences } = useMarketingAudiences()
  const { contacts } = useContacts({ fetchList: isActive })
  const { companies } = useContactCompanies({ fetchList: isActive })
  const { pickupLocations } = usePickupLocations()
  const { terminals } = useTerminals()
  const { users } = useUsers()
  const { users: salesOwners } = useSalesAssignableUsers()
  const { users: inventoryOwners } = useInventoryAssignableUsers()

  const [view, setView] = useState<CampaignsView>('campaigns')
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<MarketingCampaign | null>(null)
  const [pendingArchive, setPendingArchive] = useState<MarketingCampaign | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const [pendingSend, setPendingSend] = useState<CampaignRow | null>(null)
  const [pendingCancel, setPendingCancel] = useState<CampaignRow | null>(null)
  const [recipientsCampaign, setRecipientsCampaign] = useState<MarketingCampaign | null>(
    null,
  )

  const evalContext = useMemo(
    () =>
      buildAudienceEvalContext({
        companies,
        pickupLocations: pickupLocations ?? [],
      }),
    [companies, pickupLocations],
  )

  const userNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const user of users) {
      map.set(String(user.id), getUserDisplayName(user))
    }
    return map
  }, [users])

  const audienceNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const audience of audiences) {
      map.set(audience.id, audience.name || 'Untitled audience')
    }
    return map
  }, [audiences])

  const terminalNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const terminal of terminals) {
      map.set(terminal.id, getTerminalDisplayName(terminal))
    }
    return map
  }, [terminals])

  const filterLabels = useMemo(
    () => ({ ownerNameById: userNameById, terminalNameById }),
    [terminalNameById, userNameById],
  )

  const rows = useMemo<CampaignRow[]>(
    () =>
      campaigns
        .filter((campaign) => Boolean(campaign.archived) === showArchived)
        .map((campaign) => {
        const preview = previewAudienceEligibility(
          contacts,
          campaign.audienceFilter,
          evalContext,
          {
            includedContactIds: campaign.includedContactIds,
            excludedContactIds: campaign.excludedContactIds,
          },
        )
        const savedName = campaign.savedAudienceId
          ? audienceNameById.get(campaign.savedAudienceId)
          : undefined
        const hasSendStats =
          campaign.status === 'sent' ||
          campaign.status === 'sending' ||
          campaign.status === 'paused' ||
          campaign.status === 'cancelled' ||
          (campaign.sentCount ?? 0) > 0 ||
          (campaign.failedCount ?? 0) > 0
        return {
          id: campaign.id,
          campaign,
          name: campaign.name || 'Untitled campaign',
          subject: campaign.subject,
          typeLabel: CAMPAIGN_TYPE_LABEL[campaign.type],
          status: campaign.status,
          statusLabel: CAMPAIGN_STATUS_LABEL[campaign.status],
          audienceName: savedName ?? 'Custom filters',
          audienceSummary: summarizeAudienceFilter(campaign.audienceFilter, filterLabels),
          matchingCount: preview.matchingCount,
          eligibleCount: preview.eligibleCount,
          sentCount: hasSendStats ? campaign.sentCount ?? 0 : null,
          failedCount: hasSendStats ? campaign.failedCount ?? 0 : null,
          unsubRate:
            formatCampaignPercent(
              campaign.unsubscribedCount ?? 0,
              campaign.sentCount ?? 0,
            ) ?? PLACEHOLDER,
          scheduledAt: campaign.scheduledAt,
          repeat: parseCampaignRepeat(campaign.repeat),
          createdBy: userNameById.get(campaign.createdByUserId) ?? campaign.createdByUserId,
          updatedAt: campaign.updatedAt,
        }
      }),
    [audienceNameById, campaigns, contacts, evalContext, filterLabels, showArchived, userNameById],
  )

  const totals = useMemo(() => {
    let drafts = 0
    let sent = 0
    let recipients = 0
    for (const campaign of campaigns) {
      if (campaign.archived) continue
      if (campaign.status === 'draft') drafts += 1
      if (campaign.status === 'sent') sent += 1
      recipients += campaign.sentCount ?? 0
    }
    return {
      total: campaigns.filter((campaign) => !campaign.archived).length,
      drafts,
      sent,
      recipients,
    }
  }, [campaigns])

  const columns = useMemo<GridColDef<CampaignRow>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Campaign',
        flex: 1.4,
        minWidth: 220,
        renderCell: (params) => (
          <Stack
            sx={{
              minWidth: 0,
              width: '100%',
              height: '100%',
              justifyContent: 'center',
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} noWrap>
                {params.row.name}
              </Typography>
              {showArchived ? (
                <Chip size="small" label="Archived" color="warning" sx={{ flexShrink: 0 }} />
              ) : null}
            </Stack>
            <Typography variant="caption" color="text.secondary" noWrap>
              {params.row.subject || 'No subject yet'}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'statusLabel',
        headerName: 'Status',
        width: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.row.statusLabel}
            color={statusChipColor(params.row.status)}
            variant={params.row.status === 'draft' ? 'outlined' : 'filled'}
          />
        ),
      },
      {
        field: 'audienceName',
        headerName: 'Audience',
        flex: 1.4,
        minWidth: 200,
        renderCell: (params) => (
          <Tooltip title={params.row.audienceSummary}>
            <Stack
              sx={{
                minWidth: 0,
                width: '100%',
                height: '100%',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2" noWrap>
                {params.row.audienceName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {params.row.audienceSummary}
              </Typography>
            </Stack>
          </Tooltip>
        ),
      },
      {
        field: 'matchingCount',
        headerName: 'Matching',
        width: 105,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => formatNumber(Number(value) || 0),
      },
      {
        field: 'eligibleCount',
        headerName: 'Eligible',
        width: 105,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => (
          <Chip
            size="small"
            variant="outlined"
            color={params.row.eligibleCount > 0 ? 'success' : 'default'}
            label={formatNumber(params.row.eligibleCount)}
          />
        ),
      },
      {
        field: 'sentCount',
        headerName: 'Sent',
        width: 90,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number | null) =>
          value == null ? PLACEHOLDER : formatNumber(Number(value) || 0),
      },
      {
        field: 'failedCount',
        headerName: 'Failed',
        width: 90,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number | null) =>
          value == null ? PLACEHOLDER : formatNumber(Number(value) || 0),
      },
      {
        field: 'unsubRate',
        headerName: 'Unsub',
        width: 90,
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'scheduledAt',
        headerName: 'Scheduled',
        width: 180,
        valueFormatter: (value: Date | null) =>
          value instanceof Date && !Number.isNaN(value.getTime())
            ? value.toLocaleString('en-US')
            : PLACEHOLDER,
      },
      {
        field: 'repeat',
        headerName: 'Repeat',
        width: 110,
        valueFormatter: (value: CampaignRepeat) =>
          value && value !== 'none' ? CAMPAIGN_REPEAT_LABEL[value] : PLACEHOLDER,
      },
      { field: 'openRate', headerName: 'Open', width: 80, valueGetter: () => PLACEHOLDER },
      { field: 'clickRate', headerName: 'Click', width: 80, valueGetter: () => PLACEHOLDER },
      {
        field: 'createdBy',
        headerName: 'Created by',
        width: 150,
      },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        width: 170,
        valueFormatter: (value: Date | null) =>
          value instanceof Date && !Number.isNaN(value.getTime())
            ? value.toLocaleString('en-US')
            : PLACEHOLDER,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        width: 56,
        getActions: (params) => {
          const actions = [
            <GridActionsCellItem
              key="edit"
              icon={<Edit fontSize="small" />}
              label="Edit"
              showInMenu
              onClick={() => {
                setEditing(params.row.campaign)
                setFormOpen(true)
              }}
            />,
            <GridActionsCellItem
              key="duplicate"
              icon={<ContentCopy fontSize="small" />}
              label="Duplicate"
              showInMenu
              disabled={isSaving}
              onClick={() => {
                void duplicateCampaign(params.row.campaign.id)
              }}
            />,
            <GridActionsCellItem
              key="recipients"
              icon={<People fontSize="small" />}
              label="Recipients"
              showInMenu
              onClick={() => setRecipientsCampaign(params.row.campaign)}
            />,
          ]
          if (!showArchived) {
            actions.push(
              <GridActionsCellItem
                key="send"
                icon={<Send fontSize="small" />}
                label={params.row.status === 'sending' ? 'Continue sending' : 'Send now'}
                showInMenu
                disabled={isSending || isUpdatingStatus || !canSendCampaignRow(params.row)}
                onClick={() => setPendingSend(params.row)}
              />,
            )
          }
          if (!showArchived && canPauseCampaignRow(params.row)) {
            actions.push(
              <GridActionsCellItem
                key="pause"
                icon={<Pause fontSize="small" />}
                label="Pause"
                showInMenu
                disabled={isUpdatingStatus}
                onClick={() => {
                  void pauseCampaign(params.row.campaign.id)
                }}
              />,
            )
          }
          if (!showArchived && canResumeCampaignRow(params.row)) {
            actions.push(
              <GridActionsCellItem
                key="resume"
                icon={<PlayArrow fontSize="small" />}
                label="Resume"
                showInMenu
                disabled={isSending || isUpdatingStatus}
                onClick={() => {
                  void resumeCampaign(params.row.campaign.id)
                }}
              />,
            )
          }
          if (!showArchived && canCancelCampaignRow(params.row)) {
            actions.push(
              <GridActionsCellItem
                key="cancel"
                icon={<Cancel fontSize="small" />}
                label="Cancel campaign"
                showInMenu
                disabled={isUpdatingStatus}
                onClick={() => setPendingCancel(params.row)}
              />,
            )
          }
          if (showArchived) {
            actions.push(
              <GridActionsCellItem
                key="unarchive"
                icon={<Unarchive fontSize="small" />}
                label="Unarchive"
                showInMenu
                disabled={isArchiving}
                onClick={() => {
                  void unarchiveCampaign(params.row.campaign.id)
                }}
              />,
            )
          } else {
            actions.push(
              <GridActionsCellItem
                key="archive"
                icon={<Archive fontSize="small" />}
                label="Archive"
                showInMenu
                disabled={isArchiving}
                onClick={() => setPendingArchive(params.row.campaign)}
              />,
            )
          }
          return actions
        },
      },
    ],
    [
      duplicateCampaign,
      isArchiving,
      isSaving,
      isSending,
      isUpdatingStatus,
      pauseCampaign,
      resumeCampaign,
      showArchived,
      unarchiveCampaign,
    ],
  )

  const kpi = (icon: React.ReactNode, label: string, value: string, accent: string) => (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderRadius: 2,
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, ${alpha(accent, 0.02)} 100%)`,
        borderColor: alpha(accent, 0.25),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Avatar
          variant="rounded"
          sx={{
            bgcolor: alpha(accent, 0.18),
            color: accent,
            width: 32,
            height: 32,
            '& .MuiSvgIcon-root': { fontSize: 18 },
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.1, fontSize: '0.65rem' }}
          >
            {label}
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.15, fontSize: '1rem' }} noWrap>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )

  const handleArchive = async () => {
    if (!pendingArchive) return
    await archiveCampaign(pendingArchive.id)
    setPendingArchive(null)
  }

  const handleSend = async () => {
    if (!pendingSend) return
    await sendCampaign(pendingSend.campaign.id)
    setPendingSend(null)
  }

  const handleCancelCampaign = async () => {
    if (!pendingCancel) return
    await cancelCampaign(pendingCancel.campaign.id)
    setPendingCancel(null)
  }

  const pendingArchiveWasSent =
    pendingArchive?.status === 'sent' || pendingArchive?.status === 'sending'
  const pendingSendIsContinue = pendingSend?.status === 'sending'
  const pendingSendIsScheduled = pendingSend?.status === 'scheduled'

  if (!isActive) return null

  if (isMobile) {
    return (
      <DesktopOnlyNotice
        feature='Campaigns'
        detail='Campaigns and audiences are edited on a computer.'
      />
    )
  }

  return (
    <Box sx={{ pb: 2 }}>
      <CrmPageActions active={isActive}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditing(null)
            setFormOpen(true)
          }}
        >
          New campaign
        </Button>
      </CrmPageActions>

      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        <Grid item xs={6} sm={3}>
          {kpi(
            <CampaignIcon />,
            'Campaigns',
            formatNumber(totals.total),
            theme.palette.primary.main,
          )}
        </Grid>
        <Grid item xs={6} sm={3}>
          {kpi(<Drafts />, 'Drafts', formatNumber(totals.drafts), theme.palette.warning.main)}
        </Grid>
        <Grid item xs={6} sm={3}>
          {kpi(<Send />, 'Sent', formatNumber(totals.sent), theme.palette.success.main)}
        </Grid>
        <Grid item xs={6} sm={3}>
          {kpi(
            <MarkEmailRead />,
            'Emails delivered to',
            formatNumber(totals.recipients),
            theme.palette.info.main,
          )}
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 1.5, px: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Tabs
          value={view}
          onChange={(_, next: CampaignsView) => setView(next)}
          sx={{ minHeight: 48 }}
        >
          <Tab
            value="campaigns"
            label="Campaigns"
            icon={<CampaignIcon fontSize="small" />}
            iconPosition="start"
            sx={{ minHeight: 48, fontWeight: 700 }}
          />
          <Tab
            value="audiences"
            label={`Audiences${audiences.length > 0 ? ` (${formatNumber(audiences.length)})` : ''}`}
            icon={<FilterAlt fontSize="small" />}
            iconPosition="start"
            sx={{ minHeight: 48, fontWeight: 700 }}
          />
        </Tabs>
        {view === 'campaigns' ? (
          <FormControlLabel
            sx={{ mr: 1.5, ml: 0 }}
            control={
              <Switch
                size="small"
                checked={showArchived}
                onChange={(_, checked) => setShowArchived(checked)}
              />
            }
            label="Show archived"
          />
        ) : null}
        </Stack>
      </Paper>

      {view === 'campaigns' ? (
        isLoading ? (
          <Paper variant="outlined" sx={{ borderRadius: 3, p: 6 }}>
            <Stack alignItems="center">
              <CircularProgress />
            </Stack>
          </Paper>
        ) : rows.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 6,
              borderRadius: 3,
              textAlign: 'center',
              bgcolor: alpha(theme.palette.background.default, 0.6),
            }}
          >
            <Typography variant="h6" gutterBottom>
              {showArchived ? 'No archived campaigns' : 'No campaigns yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {showArchived
                ? 'Archived campaigns stay here with their send history. Unarchive one to put it back on the list.'
                : 'Create a campaign to pick an audience, write the email, and preview who would receive it.'}
            </Typography>
          </Paper>
        ) : (
          <Paper
            variant="outlined"
            sx={{ height: '62vh', borderRadius: 3, overflow: 'hidden' }}
          >
            <DataGridPro<CampaignRow>
              rows={rows}
              columns={columns}
              density="compact"
              disableRowSelectionOnClick
              hideFooter
              getRowHeight={() => 64}
              pinnedColumns={{ right: ['actions'] }}
              initialState={{
                sorting: { sortModel: [{ field: 'updatedAt', sort: 'desc' }] },
              }}
              onRowClick={(params) => {
                setEditing(params.row.campaign)
                setFormOpen(true)
              }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  fontWeight: 700,
                },
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-row': { cursor: 'pointer' },
              }}
            />
          </Paper>
        )
      ) : (
        <AudiencesPanel
          campaigns={campaigns}
          contacts={contacts}
          companies={companies}
          pickupLocations={pickupLocations ?? []}
          terminals={terminals}
          salesOwners={salesOwners}
          inventoryOwners={inventoryOwners}
        />
      )}

      <CampaignFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false)
          setEditing(null)
        }}
        campaign={editing}
        contacts={contacts}
        companies={companies}
        pickupLocations={pickupLocations ?? []}
        terminals={terminals}
        salesOwners={salesOwners}
        inventoryOwners={inventoryOwners}
      />

      <CampaignRecipientsDialog
        open={Boolean(recipientsCampaign)}
        onClose={() => setRecipientsCampaign(null)}
        campaignId={recipientsCampaign?.id ?? null}
        campaignName={recipientsCampaign?.name?.trim() || 'Untitled campaign'}
      />

      <Dialog
        open={Boolean(pendingArchive)}
        onClose={isArchiving ? undefined : () => setPendingArchive(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Archive fontSize="small" />
          <Typography variant="h6" fontWeight={700}>
            Archive this campaign?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              <strong>{pendingArchive?.name || 'Untitled campaign'}</strong> will be hidden
              from the campaign list. Send history stays.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pendingArchiveWasSent
                ? `This campaign already sent to ${formatNumber(pendingArchive?.sentCount ?? 0)} recipient${(pendingArchive?.sentCount ?? 0) === 1 ? '' : 's'}. Those rows stay so reporting still works. Turn on Show archived to restore it.`
                : 'Nothing has been sent from this campaign. You can restore it from Show archived.'}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => setPendingArchive(null)}
            color="inherit"
            disabled={isArchiving}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => void handleArchive()}
            disabled={isArchiving}
          >
            {isArchiving ? 'Archiving…' : 'Archive campaign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingSend)}
        onClose={isSending ? undefined : () => setPendingSend(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Send color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>
            {pendingSendIsContinue ? 'Continue sending?' : 'Send this campaign now?'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              <strong>{pendingSend?.name || 'Untitled campaign'}</strong>
              {pendingSend?.subject ? ` — ${pendingSend.subject}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pendingSendIsContinue
                ? `Send was interrupted. Continue sending to finish remaining recipients. Unsubscribed and suppressed contacts stay excluded. Marketing send does not change last sales activity.`
                : pendingSendIsScheduled
                  ? `This campaign is scheduled${pendingSend?.scheduledAt ? ` for ${pendingSend.scheduledAt.toLocaleString('en-US')}` : ''}. Send now emails ${formatNumber(pendingSend?.eligibleCount ?? 0)} eligible recipient${(pendingSend?.eligibleCount ?? 0) === 1 ? '' : 's'} immediately instead of waiting.`
                  : `This will email ${formatNumber(pendingSend?.eligibleCount ?? 0)} eligible recipient${(pendingSend?.eligibleCount ?? 0) === 1 ? '' : 's'} with the current filters. Unsubscribed and suppressed contacts stay excluded. Marketing send does not change last sales activity.`}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => setPendingSend(null)}
            color="inherit"
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSend()}
            disabled={isSending}
          >
            {isSending
              ? 'Sending…'
              : pendingSendIsContinue
                ? 'Continue sending'
                : 'Send now'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingCancel)}
        onClose={isUpdatingStatus ? undefined : () => setPendingCancel(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Cancel color="error" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>
            Cancel this campaign?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              <strong>{pendingCancel?.name || 'Untitled campaign'}</strong> will not send.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cancelled is final. Duplicate the campaign if you need a new draft. Emails
              already delivered cannot be recalled.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => setPendingCancel(null)}
            color="inherit"
            disabled={isUpdatingStatus}
          >
            Back
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleCancelCampaign()}
            disabled={isUpdatingStatus}
          >
            {isUpdatingStatus ? 'Cancelling…' : 'Cancel campaign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CampaignsTab
